// Recent searches + Recently viewed hotels + Recently viewed flights — homepage rails.
// Sits directly below the search card for conversion lift.

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Plane, Bus, Train, Car, X, ChevronRight, Star, MapPin } from 'lucide-react';
import { useRecent, clearRecentSearches, clearRecentHotels } from '../../lib/recent';
import { useCurrency } from '../../context/CurrencyContext';
import NoImagePlaceholder from '../hotels/NoImagePlaceholder';

const MODULE_ICON = { hotels: Building2, flights: Plane, buses: Bus, trains: Train, cabs: Car };

export const RecentSearchesStrip = () => {
  const items = useRecent('searches');
  const navigate = useNavigate();
  if (!items.length) return null;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-3 sm:mt-4">
      <div className="flex items-center gap-2 flex-wrap" data-testid="recent-searches-strip">
        <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mr-1">Recent searches</span>
        {items.slice(0, 3).map((it, i) => {
          const Icon = MODULE_ICON[it.module] || Building2;
          return (
            <button
              key={i}
              data-testid={`recent-search-${i}`}
              onClick={() => navigate(it.url)}
              className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs sm:text-[13px] text-gray-800 dark:text-gray-100 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
            >
              <Icon className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
              <span className="font-semibold">{it.label}</span>
              {it.sublabel && <span className="text-gray-500 dark:text-gray-400 hidden sm:inline">· {it.sublabel}</span>}
            </button>
          );
        })}
        <button
          onClick={clearRecentSearches}
          data-testid="recent-searches-clear"
          className="ml-1 text-[11px] text-gray-500 dark:text-gray-400 hover:text-red-600 font-semibold inline-flex items-center gap-1"
        >
          <X className="h-3 w-3" /> Clear
        </button>
      </div>
    </div>
  );
};

export const RecentlyViewedHotels = ({ variant = 'homepage', title, className = '' }) => {
  const items = useRecent('hotels');
  const { formatPrice } = useCurrency();
  if (!items.length) return null;

  const heading = title || (variant === 'homepage'
    ? 'Recently viewed hotels'
    : variant === 'search'
      ? 'Pick up where you left off'
      : 'You recently viewed');
  const kicker = variant === 'homepage' ? 'Pick up where you left' : 'Continue browsing';

  const linkFor = (h) => {
    const params = new URLSearchParams();
    if (h.city)      params.set('city', h.city);
    if (h.check_in)  params.set('check_in', h.check_in);
    if (h.check_out) params.set('check_out', h.check_out);
    if (h.rooms)     params.set('rooms', String(h.rooms));
    if (h.guests)    params.set('guests', String(h.guests));
    const qs = params.toString();
    return `/hotels/${h.id}${qs ? `?${qs}` : ''}`;
  };

  const starCount = (h) => {
    const n = Number(h.star_rating);
    return Number.isFinite(n) && n > 0 ? Math.max(0, Math.min(5, Math.round(n))) : 0;
  };
  const reviewScore = (h) => {
    const g = Number(h.google_rating);
    if (Number.isFinite(g) && g > 0) return { score: g, source: 'Google' };
    const r = Number(h.review_rating);
    if (Number.isFinite(r) && r > 0) return { score: r, source: null };
    return null;
  };

  return (
    <section
      className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 ${className}`}
      data-testid={`recently-viewed-hotels-${variant}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest font-semibold text-red-600 dark:text-red-400">{kicker}</p>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{heading}</h2>
        </div>
        <button
          type="button"
          onClick={clearRecentHotels}
          data-testid="recently-viewed-hotels-clear"
          className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 font-semibold inline-flex items-center gap-1"
        >
          <X className="h-3 w-3" /> Clear
        </button>
      </div>
      <div
        className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 sm:mx-0 px-4 sm:px-0"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {items.map((h) => {
          const stars = starCount(h);
          const review = reviewScore(h);
          return (
            <div
              key={h.id}
              data-testid={`viewed-hotel-${h.id}`}
              className="group relative min-w-[240px] sm:min-w-[260px] max-w-[260px] shrink-0 rounded-xl overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all hover:-translate-y-0.5"
              style={{ scrollSnapAlign: 'start' }}
            >
              <Link to={linkFor(h)} className="block">
                <div className="h-36 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  {h.image ? (
                    <img
                      src={h.image}
                      alt={h.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <NoImagePlaceholder />
                  )}
                </div>
                <div className="p-3 space-y-1">
                  <div className="flex items-start gap-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate flex-1">{h.name}</p>
                    {review && (
                      <span
                        className="shrink-0 inline-flex items-center gap-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold px-1.5 py-0.5 rounded"
                        title={review.source ? `${review.source} rating` : 'Guest rating'}
                      >
                        {review.score.toFixed(1)} <Star className="h-2.5 w-2.5 fill-current" />
                      </span>
                    )}
                  </div>
                  {stars > 0 && (
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: stars }).map((_, i) => (
                        <Star key={i} className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    {[h.area, h.city].filter(Boolean).join(', ') || '—'}
                  </p>
                  {h.price ? (
                    <p className="text-sm font-semibold text-red-600 dark:text-red-400 pt-1">
                      {formatPrice(h.price)}
                      <span className="text-[10px] text-gray-500 ml-1 font-normal">/ night</span>
                    </p>
                  ) : (
                    <p className="text-[11px] text-gray-500 pt-1">Check latest price</p>
                  )}
                </div>
              </Link>
              <div className="px-3 pb-3">
                <Link
                  to={linkFor(h)}
                  data-testid={`viewed-hotel-cta-${h.id}`}
                  className="block w-full text-center text-xs font-semibold py-2 rounded-md bg-red-600 hover:bg-red-700 text-white transition"
                >
                  View Rooms
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const RecentlyViewedFlights = () => {
  const items = useRecent('flights');
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8" data-testid="recently-viewed-flights">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest font-semibold text-red-600 dark:text-red-400">Searched recently</p>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Recently viewed flights</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.slice(0, 5).map((f) => (
          <button
            key={f.id}
            type="button"
            data-testid={`viewed-flight-${f.id}`}
            onClick={() => navigate(`/flights/search?origin=${f.origin}&destination=${f.destination}&departure_date=${f.departure_date}&trip_type=oneway&adults=1`)}
            className="text-left p-4 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-red-500 hover:shadow-md transition-all"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-bold text-gray-900 dark:text-white">{f.origin} → {f.destination}</span>
              <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-red-600" />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {f.airline || 'Flight'} · {f.departure_date}
            </p>
            {f.price ? (
              <p className="text-sm font-semibold text-red-600 dark:text-red-400 mt-2">from {formatPrice(f.price)}</p>
            ) : null}
          </button>
        ))}
      </div>
    </section>
  );
};
